// @flow @jsx h
import { Component, h } from 'preact'
import { Video, ViewportIntersections, SerumLazyResponsivePicture, HtmlContent } from '@nrk/dh-feature-components'
import { bem } from '@nrk/dh-utils'

import styles from './index.css'

import type { VisuellHistorieCardType } from '../../domain'
import { connect } from '../../../../lib/provider'
import { toVideoSrc } from '../../../common'

type Props = {
  cards: VisuellHistorieCardType[],
  align: 'left' | 'right',
  load: boolean,
  active: boolean,
  blockId: number,
  sendGaEvent: (Object) => void
}

type State = {
  activeIdx: number,
  storyMargin: string
}

class _VisuellHistorie extends Component<Props, State> {
  elm: HTMLElement
  trackedKeys = {}

  constructor () {
    super()
    this.state = {
      activeIdx: 0,
      storyMargin: '0px'
    }
  }

  componentDidMount () {
    this.updateSizes()
    window.addEventListener('resize', this.updateSizes)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.updateSizes)
  }

  updateSizes = () => {
    const firstImage = this.elm.children[0].children[0]
    const storyMargin = window.getComputedStyle(firstImage).top
    this.setState({ storyMargin })
  }

  trackOnlyOnce (key: string, action: string, label: string) {
    if (!this.trackedKeys[key]) {
      this.trackedKeys[key] = true
      this.props.sendGaEvent && this.props.sendGaEvent({ action, label })
    }
  }

  handleChange = (intersections: any[]) => {
    const activeIntersection = intersections
      .map((intersection, idx) => ({ ...intersection, idx }))
      .filter(i => i.idx % 2 === 1)
      .filter(i => i.isIntersecting && i.ratio > 0)
      .pop()

    if (!activeIntersection) {
      return
    }

    const activeIdx = (activeIntersection.idx - 1) / 2
    if (activeIdx !== this.state.activeIdx) {
      this.setState({ activeIdx })

      const key = this.props.blockId + '-' + activeIdx
      this.trackOnlyOnce(key, 'SHOW_CONTENT', `${key}/vh-card`)
    }
  }

  render () {
    const { cards, align = 'left', active } = this.props
    const { storyMargin = '0px', activeIdx } = this.state
    const negativeStoryMargin = storyMargin.startsWith('-') ? storyMargin.substring(1) : '-' + storyMargin
    const rootMargin = `${negativeStoryMargin} 0px ${negativeStoryMargin} 0px`

    return <div class={bem(styles.visuellHistorie, align, { active })} ref={elm => (this.elm = elm)}>
      <ViewportIntersections
        className={styles.visuellHistorie__content}
        onChange={this.handleChange}
        rootMargin={rootMargin}
        threshold={[0, 0.5, 1]}>
        {
          cards.map((card, index) => {
            const visible = index <= activeIdx && index > activeIdx - 2
            const first = index === 0
            const last = index === cards.length - 1
            const current = index === activeIdx

            const video = card.video && toVideoSrc(card.video)
            return [
              <div
                key={index + '-image'}
                class={bem(styles.visuellHistorie__imageItem, { visible })}>
                {
                  card.polopolyId && <SerumLazyResponsivePicture
                    className={styles.visuellHistorie__image}
                    id={card.polopolyId}
                    alt={card.alt}
                    sizes={'(min-width: 720px) 780px, 100vw'}
                    ratio={'3:4'}
                    quality={0.8}
                    load={this.props.load} />
                }
                {
                  video && <Video
                    playing={this.props.active && current}
                    poster={video.poster}
                    sources={video.sources}
                    className={styles.videoHistorie__video}
                    muted
                    playsInline
                    loop
                    preload='none'
                  />
                }
              </div>,
              <div
                key={index + '-card'}
                class={bem(styles.visuellHistorie__cardItem, { first, last, current })}>
                <div class={styles.visuellHistorie__cardGradient}>
                  <HtmlContent
                    content={card.text}
                    type={'div'}
                    className={bem(styles.visuellHistorie__card, card.linecolor)} />
                </div>
              </div>
            ]
          })
        }
      </ViewportIntersections>
    </div>
  }
}

const connector = connect()
// const connector = connect(mapContextToProps)
export const VisuellHistorie = connector(_VisuellHistorie)
